import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  type GetColumnsByCompany,
  getColumnsByCompany,
} from '../../../core/repositories/columnRepository';
import {
  type GetCompanyInfoResponse,
  getCompanyInfo,
} from '../../../core/repositories/companiesRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {ColumnListByCompanyPage} from '../../../features/columns/components/List/CompanyPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../_app';

export type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  columns: GetColumnsByCompany;
  company: GetCompanyInfoResponse;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {id} = context.query;
    if (typeof id !== 'string' || Number.isNaN(Number(id))) {
      return {
        notFound: true,
      };
    }

    const _company = await getCompanyInfo({
      cookies: context.req.cookies,
      companyId: Number(id),
    });
    if (!_company.ok) {
      return {
        notFound: true,
      };
    }
    const company = _company.value;

    const _columns = await getColumnsByCompany({companyId: id});
    if (!_columns.ok) {
      return {
        notFound: true,
      };
    }
    const columns = _columns.value;

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          columns,
          company,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    return {
      props: {
        columns,
        company,
        userDataForAnalytics: userDataResponse.ok
          ? userDataResponse.value
          : undefined,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  columns,
  company,
}: Props) => {
  const router = useRouter();
  const canonicalURL = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const title = `コラム（${company.name}）${TITLE_SUFFIX}`;
  const description = `${company.name}に関するコラムまとめ`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta key="description" name="description" content={description} />
        <meta
          key="keywords"
          name="keywords"
          content={`${company.name},コラム,就活の情報収集,外資,就活,インターンシップ,新卒採用,会社説明会`}
        />
        <link ref="canonical" href={canonicalURL} />
        <meta key="twitter:title" name="twitter:title" content={title} />
        <meta key="og:title" property="og:title" content={title} />
        <meta key="og:url" property="og:url" content={canonicalURL} />
      </Head>
      <ColumnListByCompanyPage company={company} columns={columns} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;
Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
